import { Tab, Tabs } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteAnimation } from 'src/animations/animations';
import { Divider } from 'src/components/shared/Divider/Divider';
import { TabPanel } from 'src/components/shared/Tabs/TabPanel';
import { useReduxDispatch } from 'src/store';
import { alertSuccess } from 'src/store/alerts/alerts.actions';
import {
  SpecificationDetailDto,
  SpecificationParameterDto,
  SpecificationRecipientParameterSetDto
} from 'src/store/specifications/specification';
import { mapSpecificationDetailToEdit } from 'src/store/specifications/specification.map';
import {
  createSpecification,
  editSpecification,
  SpecificationTypes
} from 'src/store/specifications/specifications.actions';
import { SpecificationApi } from 'src/store/specifications/specifications.api';
import { download, getFileNameFromHeader } from 'src/utils/download';
import { push } from 'src/utils/navigation';
import { Parameters } from './Parameters';
import { RecipientParameterSets } from './RecipientParameterSets';
import { SpecificationDetailsForm } from './SpecificationDetailsForm';

export const SpecificationPage = () => {
  const [tab, setTab] = useState<number>(0);

  const [specification, setSpecification] =
    useState<Nullable<SpecificationDetailDto>>(null);

  const [specificationParameters, setSpecificationParameters] = useState<
    SpecificationParameterDto[]
  >([]);
  const [
    specificationRecipientParameterSets,
    setSpecificationRecipientParameterSets
  ] = useState<SpecificationRecipientParameterSetDto[]>([]);

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();
  const { id: idParam } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      const response = await SpecificationApi.getById(Number(idParam));
      if (response.ok) {
        setSpecification(response.data);
        setSpecificationParameters(response.data.specificationParameters);
        setSpecificationRecipientParameterSets(
          response.data.specificationRecipientParameterSets
        );
      }
    };

    if (!!idParam) {
      fetch();
    }
  }, [idParam, dispatch]);

  const onParametersChanged = useCallback(
    (parameters: SpecificationParameterDto[]) => {
      setSpecificationParameters(parameters);
    },
    []
  );

  const onRecipientParameterSetsChanged = useCallback(
    (recipientParameterSets: SpecificationRecipientParameterSetDto[]) => {
      setSpecificationRecipientParameterSets(recipientParameterSets);
    },
    []
  );

  const onCreateVismaProduct = async () => {
    if (specification) {
      const response = await SpecificationApi.createVismaProduct(
        specification.id
      );
      if (response.ok)
        dispatch(alertSuccess(t('specification.createdVismaProduct')));
    }
  };

  const onCancel = () => {
    push('/specifications', navigate, RouteAnimation.SlideLeft);
  };

  const onSubmit = async (specification: SpecificationDetailDto) => {
    const dto = mapSpecificationDetailToEdit(specification);
    dto.specificationParameters = specificationParameters;
    dto.specificationRecipientParameterSets =
      specificationRecipientParameterSets;
    const result = dto.id
      ? await dispatch(editSpecification(dto.id, dto))
      : await dispatch(createSpecification(dto));
    if (result.type !== SpecificationTypes.Failure) {
      onCancel();
    }
  };

  const onDownloadSpecificationData = async () => {
    axios({
      method: 'GET',
      responseType: 'arraybuffer',
      url: `/specifications/${specification?.id}/specificationdata`
    })
      .then(response => {
        const disposition = response.headers['content-disposition'];
        const contentType = response.headers['content-type'];
        const filename = getFileNameFromHeader(disposition);
        const blob = new Blob([response.data as Blob], {
          type: contentType
        });
        download(filename, blob);
      })
      .catch(() => {});
  };

  return (
    <React.Fragment>
      <SpecificationDetailsForm
        specification={specification}
        onCreateVismaProduct={onCreateVismaProduct}
        onCancel={onCancel}
        onSubmit={onSubmit}
        onDownloadSpecificationData={onDownloadSpecificationData}
      />
      <Divider />
      <Tabs
        value={tab}
        onChange={(_, tab) => setTab(tab)}
        aria-label="Specification tabs"
      >
        <Tab label={t('parameter.plural')} />
        <Tab label={t('specification.recipientParameterSets')} />
      </Tabs>
      <TabPanel index={0} value={tab}>
        <Parameters
          specification={specification}
          onParametersChanged={onParametersChanged}
        />
      </TabPanel>
      <TabPanel index={1} value={tab}>
        <RecipientParameterSets
          specification={specification}
          onRecipientParameterSetsChanged={onRecipientParameterSetsChanged}
        />
      </TabPanel>
    </React.Fragment>
  );
};
